import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VALID_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const VALID_EXTS = /\.(jpg|jpeg|png|webp|heic|heif)$/i;

const uploadFileToServer = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const token = sessionStorage.getItem('inversionesfu_token');
  const response = await fetch(`${BACKEND_URL}/api/upload-image`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }

  const data = await response.json();
  if (data.success && data.url) return data.url;
  throw new Error('Upload failed');
};

const getDisplayUrl = (url) => {
  if (url && (url.startsWith('/uploads') || url.startsWith('/api/uploads'))) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

export const ImageUploader = ({ images = [], onChange, maxImages = 100 }) => {
  const [previews, setPreviews] = useState(images);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreviews(images || []);
  }, [images]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxImages - previews.length;
    if (files.length > remainingSlots) {
      toast.error(`Solo puedes agregar ${remainingSlots} imagen(es) más (máximo ${maxImages})`);
      return;
    }

    setIsProcessing(true);
    setUploadTotal(files.length);
    setUploadCurrent(0);
    const uploadedUrls = [];
    let errorCount = 0;

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadCurrent(i + 1);
        setUploadProgress(`Subiendo ${i + 1} de ${files.length}...`);

        if (!VALID_TYPES.includes(file.type.toLowerCase()) && !VALID_EXTS.test(file.name)) {
          toast.error(`Formato no soportado: ${file.name}`);
          errorCount++;
          continue;
        }

        try {
          const url = await uploadFileToServer(file);
          uploadedUrls.push(url);
        } catch (err) {
          errorCount++;
          if (errorCount <= 3) {
            toast.error(`Error: ${file.name}`);
          }
        }
      }

      if (uploadedUrls.length > 0) {
        const newPreviews = [...previews, ...uploadedUrls];
        setPreviews(newPreviews);
        onChange(newPreviews);
        toast.success(`${uploadedUrls.length} imagen(es) subida(s)${errorCount > 0 ? ` (${errorCount} con error)` : ''}`);
      }
    } catch {
      toast.error('Error al subir imágenes');
    } finally {
      setIsProcessing(false);
      setUploadProgress('');
      setUploadCurrent(0);
      setUploadTotal(0);
      e.target.value = '';
    }
  }, [maxImages, previews, onChange]);

  const handleRemoveImage = useCallback((index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    onChange(newPreviews);
  }, [previews, onChange]);

  const progressPercent = uploadTotal > 0 ? Math.round((uploadCurrent / uploadTotal) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="image-uploader">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">Imágenes del producto</label>
        <span className="text-xs text-muted-foreground">{previews.length}/{maxImages}</span>
      </div>

      {previews.length < maxImages && (
        <div data-testid="image-upload-area"
          onClick={() => !isProcessing && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-4 bg-[#121212] transition-colors cursor-pointer ${
            isProcessing ? 'border-[#39FF14] opacity-70' : 'border-border hover:border-[#39FF14]/50'}`}>
          <input ref={fileInputRef} type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
            multiple onChange={handleFileSelect} className="hidden" disabled={isProcessing} data-testid="image-file-input" />
          <div className="flex flex-col items-center justify-center text-center">
            {isProcessing ? (
              <>
                <Loader2 className="h-8 w-8 text-[#39FF14] mb-2 animate-spin" />
                <p className="text-sm font-medium text-[#39FF14]">{uploadProgress || 'Procesando...'}</p>
                <div className="w-full mt-2 bg-[#2A2A2A] rounded-full h-2">
                  <div className="bg-[#39FF14] h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{progressPercent}%</p>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm font-medium text-foreground">Click para subir imágenes</p>
                <p className="text-xs text-muted-foreground">JPG, PNG, WEBP o HEIC - Hasta {maxImages} imágenes</p>
              </>
            )}
          </div>
        </div>
      )}

      {previews.length > 0 && (
        <div className={`grid grid-cols-4 gap-2 ${previews.length > 12 ? 'max-h-[300px] overflow-y-auto pr-1' : ''}`}>
          {previews.map((preview, index) => (
            <div key={`${preview}-${index}`} className="relative group aspect-square rounded-lg overflow-hidden bg-[#121212] border border-border">
              <img src={getDisplayUrl(preview)} alt={`Imagen ${index + 1}`}
                className="w-full h-full object-cover" data-testid={`image-preview-${index}`}
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23222" width="100" height="100"/><text fill="%23666" x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em">Error</text></svg>';
                }} />
              <button type="button" data-testid={`remove-image-${index}`}
                onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                className="absolute top-1 right-1 p-1.5 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="h-3 w-3 text-white" />
              </button>
              {index === 0 && (
                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-[#39FF14] text-black text-[10px] font-bold rounded">Principal</div>
              )}
            </div>
          ))}
        </div>
      )}

      {previews.length === 0 && !isProcessing && (
        <p className="text-xs text-muted-foreground text-center">Las imágenes se comprimen automáticamente en el servidor</p>
      )}
    </div>
  );
};
